/**
 * The engine draws on `<canvas>` on the page and on `OffscreenCanvas` in its Worker, where there is
 * no `document`. These helpers hide the difference.
 */
export type CanvasLike = HTMLCanvasElement | OffscreenCanvas;
export type Context2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export function makeCanvas(width: number, height: number): CanvasLike {
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }
  return new OffscreenCanvas(width, height);
}

export function context2d(canvas: CanvasLike, readsFrequently = false): Context2D {
  const ctx = (
    canvas as { getContext(id: '2d', options?: CanvasRenderingContext2DSettings): Context2D | null }
  ).getContext('2d', readsFrequently ? { willReadFrequently: true } : undefined);
  if (!ctx) throw new Error('2D canvas context unavailable');
  return ctx;
}

export function readPixels(canvas: CanvasLike): ImageData {
  return context2d(canvas, true).getImageData(0, 0, canvas.width, canvas.height);
}

export function canvasFromPixels(image: ImageData): CanvasLike {
  const canvas = makeCanvas(image.width, image.height);
  context2d(canvas).putImageData(image, 0, 0);
  return canvas;
}

/**
 * Frees a canvas's pixel memory now. iOS Safari caps the total canvas memory of a page (about
 * 384 MB) and keeps a dropped canvas's backing store for a while; shrinking it to 0×0 gives it back.
 */
export function releaseCanvas(canvas: CanvasLike | null | undefined): void {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
}
